import React, { useState, useEffect } from 'react';
import { Scale } from 'lucide-react';
import { SEO } from '../components/SEO';

interface CalorieState {
  age: string;
  gender: 'male' | 'female';
  weight: string;
  height: string;
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very-active';
  goal: 'maintain' | 'lose' | 'gain';
}

const activityMultipliers = {
  sedentary: 1.2, // Little or no exercise
  light: 1.375, // Light exercise 1-3 times/week
  moderate: 1.55, // Moderate exercise 3-5 times/week
  active: 1.725, // Heavy exercise 6-7 times/week
  'very-active': 1.9 // Very heavy exercise, physical job
};

const goalMultipliers = {
  maintain: 1,
  lose: 0.85, // 15% deficit
  gain: 1.15 // 15% surplus
};

function KalkulatorKalorija() {
  const [values, setValues] = useState<CalorieState>({
    age: '',
    gender: 'male',
    weight: '',
    height: '',
    activityLevel: 'moderate',
    goal: 'maintain'
  });

  const [results, setResults] = useState<{
    bmr: number;
    tdee: number;
    targetCalories: number;
    macros: {
      protein: number;
      carbs: number;
      fats: number;
    };
  } | null>(null);

  const calculateBMR = () => {
    const weight = parseFloat(values.weight);
    const height = parseFloat(values.height);
    const age = parseFloat(values.age);

    if (isNaN(weight) || isNaN(height) || isNaN(age)) {
      return null;
    }

    // Mifflin-St Jeor Formula
    let bmr = values.gender === 'male'
      ? (10 * weight) + (6.25 * height) - (5 * age) + 5
      : (10 * weight) + (6.25 * height) - (5 * age) - 161;

    const tdee = bmr * activityMultipliers[values.activityLevel];
    const targetCalories = tdee * goalMultipliers[values.goal];

    // Calculate macros (40/30/30 split for maintain, adjusted for goals)
    let proteinRatio = 0.3;
    let carbsRatio = 0.4;
    let fatsRatio = 0.3;

    if (values.goal === 'lose') {
      proteinRatio = 0.4;
      carbsRatio = 0.3;
      fatsRatio = 0.3;
    } else if (values.goal === 'gain') {
      proteinRatio = 0.3;
      carbsRatio = 0.45;
      fatsRatio = 0.25;
    }

    const macros = {
      protein: Math.round((targetCalories * proteinRatio) / 4), // 4 calories per gram
      carbs: Math.round((targetCalories * carbsRatio) / 4), // 4 calories per gram
      fats: Math.round((targetCalories * fatsRatio) / 9) // 9 calories per gram
    };

    return {
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      targetCalories: Math.round(targetCalories),
      macros
    };
  };

  useEffect(() => {
    if (values.age && values.weight && values.height) {
      const results = calculateBMR();
      setResults(results);
    }
  }, [values]);

  const getActivityDescription = (level: string): string => {
    switch (level) {
      case 'sedentary':
        return 'Uredski posao, bez vježbanja';
      case 'light':
        return 'Lagano vježbanje 1-3x tjedno';
      case 'moderate':
        return 'Umjereno vježbanje 3-5x tjedno';
      case 'active':
        return 'Intenzivno vježbanje 6-7x tjedno';
      case 'very-active':
        return 'Vrlo intenzivno vježbanje, fizički posao';
      default:
        return '';
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Kalorija",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte svoje dnevne potrebe za kalorijama. Precizni izračuni bazirani na vašoj visini, težini i razini aktivnosti.",
    "url": "https://online-kalkulator.com/kalkulator-kalorija",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun dnevnih kalorija",
      "Izračun makronutrijenata",
      "Prilagodba razini aktivnosti",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Kalorija | Izračun Dnevnih Kalorija"
        description="Izračunajte svoje dnevne potrebe za kalorijama. Precizni izračuni bazirani na vašoj visini, težini i razini aktivnosti."
        canonicalUrl="/kalkulator-kalorija"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Scale className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Kalorija</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dob
                  </label>
                  <input
                    type="number"
                    value={values.age}
                    onChange={(e) => setValues(prev => ({ ...prev, age: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spol
                  </label>
                  <select
                    value={values.gender}
                    onChange={(e) => setValues(prev => ({ ...prev, gender: e.target.value as 'male' | 'female' }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="male">Muško</option>
                    <option value="female">Žensko</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Težina (kg)
                  </label>
                  <input
                    type="number"
                    value={values.weight}
                    onChange={(e) => setValues(prev => ({ ...prev, weight: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 70"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina (cm)
                  </label>
                  <input
                    type="number"
                    value={values.height}
                    onChange={(e) => setValues(prev => ({ ...prev, height: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 175"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razina aktivnosti
                </label>
                <select
                  value={values.activityLevel}
                  onChange={(e) => setValues(prev => ({ ...prev, activityLevel: e.target.value as CalorieState['activityLevel'] }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="sedentary">Sjedilački način života</option>
                  <option value="light">Lagana aktivnost</option>
                  <option value="moderate">Umjerena aktivnost</option>
                  <option value="active">Aktivni način života</option>
                  <option value="very-active">Vrlo aktivni način života</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  {getActivityDescription(values.activityLevel)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cilj
                </label>
                <select
                  value={values.goal}
                  onChange={(e) => setValues(prev => ({ ...prev, goal: e.target.value as CalorieState['goal'] }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="maintain">Održavanje težine</option>
                  <option value="lose">Gubitak težine</option>
                  <option value="gain">Dobivanje težine</option>
                </select>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-6 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">BMR</div>
                      <div className="text-xl font-bold text-blue-600">{results.bmr} kcal</div>
                      <div className="text-xs text-gray-500">Bazalni metabolizam</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">TDEE</div>
                      <div className="text-xl font-bold text-blue-600">{results.tdee} kcal</div>
                      <div className="text-xs text-gray-500">Ukupna potrošnja</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ciljane kalorije</div>
                      <div className="text-xl font-bold text-blue-600">{results.targetCalories} kcal</div>
                      <div className="text-xs text-gray-500">Za vaš cilj</div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Preporučeni makronutrijenti</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-gray-600">Proteini</div>
                        <div className="text-lg font-semibold">{results.macros.protein}g</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Ugljikohidrati</div>
                        <div className="text-lg font-semibold">{results.macros.carbs}g</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Masti</div>
                        <div className="text-lg font-semibold">{results.macros.fats}g</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Kalorija</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator kalorija je alat koji vam pomaže izračunati dnevne energetske potrebe
                  na temelju vaših fizičkih karakteristika i razine aktivnosti. Koristi znanstveno
                  potvrđenu Mifflin-St Jeor formulu za izračun bazalnog metabolizma (BMR) i
                  prilagođava rezultate prema vašoj razini aktivnosti i ciljevima.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što Rezultati Znače</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>BMR (Bazalni metabolizam) - Kalorije koje tijelo troši u mirovanju</li>
                  <li>TDEE (Ukupna dnevna potrošnja energije) - BMR prilagođen razini aktivnosti</li>
                  <li>Ciljane kalorije - Prilagođeni unos za vaš specifični cilj</li>
                  <li>Makronutrijenti - Preporučeni omjeri proteina, ugljikohidrata i masti</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Rezultate</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Pratite dnevni unos kalorija prema ciljanoj vrijednosti</li>
                  <li>Rasporedite obroke tijekom dana</li>
                  <li>Prilagodite unos makronutrijenata prema preporukama</li>
                  <li>Redovito pratite napredak i prilagođavajte unos po potrebi</li>
                  <li>Kombinirajte s redovitom tjelesnom aktivnošću</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Rezultati su procjena bazirana na prosječnim vrijednostima</li>
                  <li>Individualne potrebe mogu varirati</li>
                  <li>Konzultirajte se s liječnikom prije značajnih promjena u prehrani</li>
                  <li>Postupno mijenjajte prehrambene navike</li>
                  <li>Redovito pratite tjelesnu težinu i prilagođavajte unos</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Uspjeh</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Vodite dnevnik prehrane</li>
                  <li>Planirajte obroke unaprijed</li>
                  <li>Jedite raznovrsnu hranu</li>
                  <li>Održavajte redovitu tjelesnu aktivnost</li>
                  <li>Pijte dovoljno vode</li>
                  <li>Osigurajte kvalitetan san</li>
                  <li>Budite strpljivi i dosljedni</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorKalorija;